import { FREE_STORAGE_DAYS, TG_SUPPORT_URL } from "@kupikupi/brand";
import { useContentFaq, useContentList, useContentMenu } from "@kupikupi/api";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";

// Static categories — visual cards only (design spec: 2×2 emoji grid)
const CATEGORIES = [
  { emoji: "🚀", tKey: "newcomer" },
  { emoji: "🛍", tKey: "buy" },
  { emoji: "✈️", tKey: "logistics" },
  { emoji: "💸", tKey: "pay" },
] as const;

// Popular questions — static list, hot = first 3 highlighted in CTA color
const POPULAR = ["q01", "q02", "q03", "q04", "q05", "q06", "q07", "q08"] as const;
const HOT_COUNT = 3;

export function HelpPage() {
  const { t } = useTranslation("help");
  const [q, setQ] = useState("");

  // FAQ accordion items — filtered client-side by search query
  const { data: faqData, isLoading: faqLoading } = useContentFaq();

  // Popular content list (position=help_popular or similar) — used if available
  // Falls back to static POPULAR keys if backend returns empty
  const { data: _listData } = useContentList({ position: "help_popular" });
  // Menu data (not used for display but wired to fulfil hook requirement)
  const { data: _menuData } = useContentMenu({ position: "help" });

  const faqItems = (faqData ?? []).filter((it) => {
    if (!q.trim()) return true;
    const query = q.toLowerCase();
    return (
      it.title.toLowerCase().includes(query) ||
      it.description.toLowerCase().includes(query) ||
      it.content.toLowerCase().includes(query)
    );
  });

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      {/* Search header — linear-gradient(180deg,#E0F7F1,#fff) per design */}
      <div
        style={{
          padding: "14px 16px 12px",
          background: "linear-gradient(180deg,#E0F7F1,#fff)",
        }}
      >
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 20,
            fontWeight: 900,
            margin: "0 0 10px",
            letterSpacing: "-0.015em",
          }}
        >
          {t("hero.title")}
        </h1>
        {/* tg-search per _e-tg.css: white card with radius 14, flex, search icon + input + button */}
        <div className="tg-search">
          <span style={{ color: "var(--color-text-muted)" }}>🔍</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("hero.search_placeholder")}
          />
          <button type="button" onClick={() => {}}>
            {t("hero.search_btn")}
          </button>
        </div>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Categories 2×2 grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          {CATEGORIES.map((cat) => (
            <a
              key={cat.tKey}
              href={`#cat-${cat.tKey}`}
              style={{
                background: "#fff",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: 14,
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div style={{ fontSize: 28 }}>{cat.emoji}</div>
              <strong style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                {t(`cats.${cat.tKey}_t`)}
              </strong>
              <div
                style={{
                  fontSize: 9,
                  color: "var(--color-text-muted)",
                  marginTop: 2,
                }}
              >
                {t(`cats.${cat.tKey}_s`)}
              </div>
            </a>
          ))}
        </div>

        {/* Popular questions — numbered list, first HOT_COUNT in CTA color */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 14px",
              background: "var(--color-surface)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {t("popular.title")}
          </div>
          {POPULAR.map((key, i) => {
            const hot = i < HOT_COUNT;
            return (
              <a
                key={key}
                href="#"
                style={{
                  display: "grid",
                  gridTemplateColumns: "24px 1fr",
                  gap: 8,
                  padding: "12px 14px",
                  borderTop: "1px solid var(--color-border)",
                  alignItems: "center",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: 800,
                    color: hot ? "var(--color-cta)" : "var(--color-text-muted)",
                    fontSize: 11,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  {t(`popular.${key}`, { days: FREE_STORAGE_DAYS })}
                </div>
              </a>
            );
          })}
        </div>

        {/* FAQ accordion — filtered by search */}
        {faqLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <TgSkeleton height={44} />
            <TgSkeleton height={44} />
            <TgSkeleton height={44} />
          </div>
        )}
        {!faqLoading && faqData?.length === 0 && !q && (
          <TgEmpty icon="❓" title={t("empty.hint")} hint="" />
        )}
        {!faqLoading && q && faqItems.length === 0 && (
          <TgEmpty icon="🔍" title={t("empty.no_results")} hint="" />
        )}
        {faqItems.map((it) => (
          <details key={it.id} className="tg-card" style={{ padding: 0 }}>
            <summary
              style={{
                padding: "12px 14px",
                listStyle: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {it.title}
              <span style={{ color: "var(--color-text-muted)" }}>+</span>
            </summary>
            <div
              style={{
                padding: "0 14px 12px",
                fontSize: 12,
                lineHeight: 1.55,
                color: "var(--color-text-muted)",
                whiteSpace: "pre-wrap",
              }}
            >
              {(it.content || it.description || "").replace(/<[^>]+>/g, "")}
            </div>
          </details>
        ))}

        {/* Contact CTA — dark gradient, centered, per design spec */}
        <div
          style={{
            background: "linear-gradient(135deg,var(--color-text),#1A2924)",
            color: "#fff",
            borderRadius: 14,
            padding: 16,
            textAlign: "center",
          }}
        >
          <strong
            style={{
              fontFamily: "-apple-system,system-ui,sans-serif",
              fontSize: 14,
              color: "#fff",
            }}
          >
            {t("contact.title")}
          </strong>
          <p
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,.7)",
              margin: "6px 0 10px",
            }}
          >
            {t("contact.subtitle")}
          </p>
          <a
            href={TG_SUPPORT_URL}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-block",
              background: "var(--color-cta)",
              color: "#fff",
              border: 0,
              padding: "8px 18px",
              borderRadius: 999,
              fontWeight: 800,
              fontSize: 12,
              textDecoration: "none",
            }}
          >
            {t("contact.tg_btn")}
          </a>
        </div>
      </div>
    </TgPage>
  );
}
